import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown } from '@ui/icons'
import { cx } from '@ui/classes'
import { sectionCard } from '@ui/admin'

const STORAGE_PREFIX = 'admin.card.'

export interface AdminCardProps {
  /** Стабильный id окна: ключ для localStorage. */
  id: string
  title: string
  /** Короткая подпись или счётчик рядом с заголовком. */
  meta?: ReactNode
  /** Кнопки справа от заголовка (доступны и в свёрнутом виде). */
  actions?: ReactNode
  /** Развёрнуто ли по умолчанию. */
  defaultOpen?: boolean
  /** Окно на всю высоту браузера (например, активный каталог). */
  fullHeight?: boolean
  children: ReactNode
  className?: string
  /** data-testid для e2e. */
  testId?: string
}

/**
 * Сворачиваемое окно админки.
 *
 * Свёрнутый вид — одна строка: заголовок и круглая кнопка-шеврон. Состояние
 * запоминается в localStorage, поэтому директор не теряет настроенный вид
 * при переходах между вкладками.
 */
export function AdminCard({
  id,
  title,
  meta,
  actions,
  defaultOpen = false,
  fullHeight = false,
  children,
  className,
  testId
}: AdminCardProps) {
  const storageKey = `${STORAGE_PREFIX}${id}`
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (raw === '1') return true
      if (raw === '0') return false
    } catch {
      // Приватный режим: работает только значение по умолчанию.
    }
    return defaultOpen
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, open ? '1' : '0')
    } catch {
      // storage недоступен — не критично.
    }
  }, [open, storageKey])

  return (
    <div
      data-testid={testId}
      className={cx(
        sectionCard,
        fullHeight && open && 'flex min-h-[calc(100vh-140px)] flex-col',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="m-0 truncate text-big font-bold text-ink">{title}</h3>
          {meta}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <button
            type="button"
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-lineStrong bg-white text-muted transition-colors hover:bg-soft hover:text-ink"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-label={open ? `Свернуть «${title}»` : `Развернуть «${title}»`}
            title={open ? 'Свернуть' : 'Развернуть'}
          >
            <ChevronDown
              size={16}
              className={cx('transition-transform', open && 'rotate-180')}
            />
          </button>
        </div>
      </div>
      {open && (
        <div className={cx('mt-3', fullHeight && 'min-h-0 flex-1')}>{children}</div>
      )}
    </div>
  )
}
